import React, { useMemo, useState } from 'react';
import { Select, Tag, Space, Checkbox } from 'antd';

/**
 * A reusable group selector for choosing WeWork groups.
 *
 * Props:
 * - groups: array of group objects to display
 * - value: controlled value (single or array)
 * - onChange: change handler
 * - mode: 'multiple' | undefined
 * - placeholder: string
 * - style: object
 * - disabled: boolean
 * - allowClear: boolean
 * - valueKey: which field of group to use as value (default: 'id')
 * - labelKey: which field of group to use as label (default: 'name')
 * - ownerNameKey: which field holds owner name (default: 'ownerName')
 * - showOwner: whether to show owner in option label (default: true)
 * - renderOptionLabel: optional custom renderer (group) => ReactNode
 * - valueType: 'raw' | 'string' - whether to return raw value or stringified (default: 'raw')
 * - maxDisplayedTags: number - max number of selected tags to display (default: 5)
 */
const GroupSelect = ({
  groups = [],
  value,
  onChange,
  mode,
  placeholder,
  style,
  disabled,
  allowClear = true,
  valueKey = 'id',
  labelKey = 'name',
  ownerNameKey = 'ownerName',
  showOwner = false,
  renderOptionLabel,
  valueType = 'raw',
  maxDisplayedTags = 10
}) => {
  const [searchText, setSearchText] = useState('');

  const matchFn = (group, input) => {
    if (!input) return true;
    try {
      const name = String(group[labelKey] || '').toLowerCase();
      const owner = String(group[ownerNameKey] || group.owner || '').toLowerCase();
      const kw = String(input).toLowerCase();
      return name.includes(kw) || owner.includes(kw);
    } catch (e) {
      return false;
    }
  };

  const filteredGroups = useMemo(() => {
    if (!Array.isArray(groups)) return [];
    return groups.filter(g => g && (g[valueKey] !== undefined && g[valueKey] !== null) && matchFn(g, searchText));
  }, [groups, valueKey, searchText]);

  const allOptions = useMemo(() => {
    return filteredGroups.map(group => {
      const rawValue = group[valueKey];
      const key = String(rawValue);
      const name = group[labelKey] || '';
      // const ownerDisplay = group[ownerNameKey] || group.owner || '';

      const defaultContent = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 500 }}>{name}</span>
          {/* {showOwner && ownerDisplay ? (
            <span style={{ marginLeft: 8, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
              群主: {ownerDisplay}
            </span>
          ) : null} */}
        </div>
      );

      const contentNode = renderOptionLabel ? renderOptionLabel(group) : defaultContent;
      const optionValue = valueType === 'string' ? String(rawValue) : rawValue;

      return (
        <Select.Option key={key} value={optionValue}>
          {contentNode}
        </Select.Option>
      );
    });
  }, [filteredGroups, valueKey, labelKey, ownerNameKey, showOwner, renderOptionLabel, valueType]);

  const currentValuesArray = useMemo(() => {
    if (mode === 'multiple') {
      return Array.isArray(value) ? value : [];
    }
    return value !== undefined && value !== null ? [value] : [];
  }, [value, mode]);

  const visibleOptionValues = useMemo(() => {
    return filteredGroups.map(g => (valueType === 'string' ? String(g[valueKey]) : g[valueKey]));
  }, [filteredGroups, valueKey, valueType]);

  const selectedVisibleCount = useMemo(() => {
    const visibleSet = new Set(visibleOptionValues.map(v => String(v)));
    return currentValuesArray.reduce((acc, v) => acc + (visibleSet.has(String(v)) ? 1 : 0), 0);
  }, [visibleOptionValues, currentValuesArray]);

  const allVisibleSelected = useMemo(() => {
    return visibleOptionValues.length > 0 && selectedVisibleCount === visibleOptionValues.length;
  }, [visibleOptionValues, selectedVisibleCount]);

  const isIndeterminate = useMemo(() => {
    return selectedVisibleCount > 0 && selectedVisibleCount < visibleOptionValues.length;
  }, [selectedVisibleCount, visibleOptionValues]);

  const onToggleSelectVisible = () => {
    if (disabled || mode !== 'multiple' || visibleOptionValues.length === 0) return;
    if (allVisibleSelected) {
      // 反选为全不选（对可见项）
      const visibleSet = new Set(visibleOptionValues.map(v => String(v)));
      const newValues = currentValuesArray.filter(v => !visibleSet.has(String(v)));
      onChange && onChange(newValues);
    } else {
      // 全选可见
      const currentKeys = new Set(currentValuesArray.map(v => String(v)));
      const newOnes = visibleOptionValues.filter(v => !currentKeys.has(String(v)));
      const newValues = [...currentValuesArray, ...newOnes];
      onChange && onChange(newValues);
    }
  };

  const dropdownHeader = (
    <div style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
      <Space size={8} wrap>
        <Checkbox
          indeterminate={isIndeterminate}
          checked={allVisibleSelected}
          disabled={disabled || mode !== 'multiple' || visibleOptionValues.length === 0}
          onChange={onToggleSelectVisible}
        >
          {allVisibleSelected ? '反选' : '全选'}
        </Checkbox>
        <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
          {selectedVisibleCount}/{visibleOptionValues.length}（可见），共 {Array.isArray(groups) ? groups.length : 0}
        </span>
      </Space>
    </div>
  );

  return (
    <Select
      mode={mode}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={style}
      disabled={disabled}
      allowClear={allowClear}
      showSearch
      filterOption={false}
      onSearch={setSearchText}
      maxTagCount={maxDisplayedTags}
      dropdownRender={(menu) => (
        <div>
          {mode === 'multiple' && dropdownHeader}
          {menu}
        </div>
      )}
    >
      {allOptions}
    </Select>
  );
};

export default GroupSelect;


